import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Typography, TextField, FormControl, Select, MenuItem, Avatar, Button, InputAdornment, FormHelperText} from '@mui/material'
import {Edit, Search} from '@mui/icons-material'
import {parseError, getColorFromName, getInitials, ignoreTimezone} from '../../../../utils/helpers'
import {AddressDetail} from '..'
import {clientApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {format} from 'date-fns'
import {SkeletonContainer, FieldSkeleton, TextSkeleton, ButtonSkeleton} from '../../../../components'

const SelectSkeleton = () => (
    <SkeletonContainer>
        <FieldSkeleton label={false} sx={{mb: '6px'}} />
    </SkeletonContainer>
)

// loading: the parent is still fetching the schedule being edited.
export default ({form, loading = false, setLookupsSkeleton}) => {
    const {setMessageBox, branchId} = useGlobalStates()
    const [clientsSkeleton, setClientsSkeleton] = useState(true)
    const [clientSkeleton, setClientSkeleton] = useState(false)
    const clientRequestId = useRef(0)
    const [open, setOpen] = useState(false)
    const [clients, setClients] = useState([])
    const [selectedClient, setSelectedClient] = useState(null)
    const [searchClientTerm, setSearchClientTerm] = useState('')
    const filteredClients = clients?.filter(item => item?.display_name?.toLowerCase().includes(searchClientTerm?.toLowerCase()))
    const [caregivers, setCaregivers] = useState([])
    const [selectedCaregiver, setSelectedCaregiver] = useState(null)
    const [searchCaregiverTerm, setSearchCaregiverTerm] = useState('')
    const filteredCaregivers = caregivers?.filter(item => item?.display_name?.toLowerCase().includes(searchCaregiverTerm?.toLowerCase()))
    const [serviceAgreement, setServiceAgreement] = useState(null)
    const [clientAddress, setClientAddress] = useState(null)

    useEffect(() => {
        if (branchId) {
            setClientsSkeleton(true)
            clientApi
                .getBranchClients(branchId, {limit: 100})
                .then(
                    ({
                        data: {
                            data: {data},
                        },
                    }) => {
                        setClientsSkeleton(false)
                        setClients(data?.map(item => ({...item, display_name: item?.first_name + ' ' + item?.last_name})))

                        const clientId = form.getValues('client_id')
                        if (clientId) {
                            setSelectedClient(clientId)
                        }
                    },
                )
                .catch(error => {
                    setClientsSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }, [branchId])

    // The parent keeps Next disabled while the chosen client's lookups are loading.
    useEffect(() => {
        setLookupsSkeleton?.(clientSkeleton)
    }, [clientSkeleton])

    useEffect(() => () => setLookupsSkeleton?.(false), [])

    useEffect(() => {
        // Each client pick gets an id; results for a client that is no longer selected are dropped.
        const id = ++clientRequestId.current
        const current = () => id === clientRequestId.current
        ;(async () => {
            if (selectedClient) {
                setClientSkeleton(true)

                form.setValue('client_id', selectedClient)
                form.setValue('service_agreement_id', undefined)
                form.setValue('address_id', undefined)

                try {
                    const {
                        data: {caregivers: data1},
                    } = await clientApi.getClientCaregivers(branchId, selectedClient)
                    if (!current()) return
                    setCaregivers(
                        data1?.map(item => ({
                            ...item?.caregiver,
                            display_name: item?.caregiver.first_name + ' ' + item?.caregiver.last_name,
                            service_items: item?.services,
                        })),
                    )

                    const {
                        data: {data: data2},
                    } = await clientApi.getServiceAgreements(selectedClient, {include_items: true})
                    if (!current()) return
                    const serviceAgreementId = data2?.find(item => item.status === 'active')?.id
                    form.setValue('service_agreement_id', serviceAgreementId)

                    const {
                        data: {data: data3},
                    } = await clientApi.getServiceAgreement(selectedClient, serviceAgreementId)
                    if (!current()) return
                    setServiceAgreement(data3)

                    const {
                        data: {data: data4},
                    } = await clientApi.getClientAddresses(selectedClient)
                    if (!current()) return
                    const address = data4?.find(item => item?.type == 'primary')
                    setClientAddress({...address, notes: form.getValues('notes')})
                    form.setValue('address_id', address?.id)

                    const caregiverId = form.getValues('caregiver_id')
                    if (caregiverId) {
                        setSelectedCaregiver(caregiverId)
                    }
                } catch (error) {
                    if (!current()) return
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                }

                setClientSkeleton(false)
            }
        })()
    }, [selectedClient])

    useEffect(() => {
        if (selectedCaregiver) {
            form.setValue('caregiver_id', selectedCaregiver)

            const selectedCaregiverServiceItems = caregivers?.find(item => item?.id === selectedCaregiver)?.service_items
            const caregiverAssignedServiceItems = serviceAgreement?.service_items?.filter(
                item1 => item1?.status == 'active' && selectedCaregiverServiceItems?.some(item2 => item2?.service_id === item1?.service_id),
            )
            form.setValue(
                'service_items',
                caregiverAssignedServiceItems?.map(item => ({
                    id: item?.id,
                    display_name: `[${format(new Date(ignoreTimezone(item?.start_date)), 'MM/dd/yyyy')} - ${format(new Date(ignoreTimezone(item?.end_date)), 'MM/dd/yyyy')}] ${item?.service_name}`,
                    start_date: item?.start_date ? new Date(ignoreTimezone(item?.start_date)) : '',
                    end_date: item?.end_date ? new Date(ignoreTimezone(item?.end_date)) : '',
                    total_cost: item?.total_amount,
                    activities: item?.activities,
                })) || [],
            )
        }
    }, [selectedCaregiver])

    useEffect(() => {
        if (clientAddress) {
            form.setValue('address', clientAddress)
            form.setValue('notes', clientAddress?.notes)
        }
    }, [clientAddress])

    const clientChange = event => {
        setSelectedClient(event.target.value)
        setSelectedCaregiver(null)
        // The caregiver belongs to the previous client: drop it, or it would be restored once this client loads.
        form.setValue('caregiver_id', '')
        form.setValue('service_items', [])
        form.clearErrors('client_id')
    }

    const renderClientLabel = selected => {
        const client = clients.find(item => item.id === selected)
        return client ? client.display_name : 'Search or select a client'
    }

    const caregiverChange = event => {
        setSelectedCaregiver(event.target.value)
        form.clearErrors('caregiver_id')
    }

    const renderCaregiverLabel = selected => {
        const caregiver = caregivers.find(item => item.id === selected)
        return caregiver ? caregiver.display_name : 'Search or select a caregiver'
    }

    return (
        <>
            <Typography variant="inputLabel" color="text.secondary">
                Client Name{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            {(loading || clientsSkeleton) && <SelectSkeleton />}
            <FormControl
                fullWidth
                error={!!form.formState.errors['client_id']}
                style={{marginBottom: 6, display: loading || clientsSkeleton ? 'none' : undefined}}>
                <Select
                    disabled={clientSkeleton}
                    value={selectedClient ?? ''}
                    onChange={clientChange}
                    renderValue={renderClientLabel}
                    displayEmpty
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                                overflow: 'auto',
                            },
                        },
                    }}>
                    <MenuItem disableRipple disableTouchRipple>
                        <TextField
                            placeholder="Search client name..."
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchClientTerm}
                            onChange={e => setSearchClientTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => e.stopPropagation()}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </MenuItem>
                    {filteredClients.map(item => (
                        <MenuItem key={item.id} value={item.id}>
                            <div className="people-card">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 32, height: 32}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.first_name + ' ' + item?.last_name),
                                            width: 32,
                                            height: 32,
                                            fontSize: 16,
                                        }}>
                                        {getInitials(item?.first_name + ' ' + item?.last_name)}
                                    </Avatar>
                                )}
                                <Typography variant="body2" color="text.primary">
                                    {item?.first_name + ' ' + item?.last_name}
                                </Typography>
                            </div>
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>{form.formState.errors['client_id']?.message}</FormHelperText>
            </FormControl>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Caregiver Name{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            {(loading || clientsSkeleton || clientSkeleton) && <SelectSkeleton />}
            <FormControl
                fullWidth
                error={!!form.formState.errors['caregiver_id']}
                style={{marginBottom: 6, display: loading || clientsSkeleton || clientSkeleton ? 'none' : undefined}}>
                <Select
                    value={selectedCaregiver ?? ''}
                    onChange={caregiverChange}
                    renderValue={renderCaregiverLabel}
                    displayEmpty
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                                overflow: 'auto',
                            },
                        },
                    }}>
                    <MenuItem disableRipple disableTouchRipple>
                        <TextField
                            placeholder="Search caregiver name..."
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchCaregiverTerm}
                            onChange={e => setSearchCaregiverTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => e.stopPropagation()}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </MenuItem>
                    {filteredCaregivers.map(item => (
                        <MenuItem key={item.id} value={item.id}>
                            <div className="people-card">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 32, height: 32}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.first_name + ' ' + item?.last_name),
                                            width: 32,
                                            height: 32,
                                            fontSize: 16,
                                        }}>
                                        {getInitials(item?.first_name + ' ' + item?.last_name)}
                                    </Avatar>
                                )}
                                <Typography variant="body2" color="text.primary">
                                    {item?.first_name + ' ' + item?.last_name}
                                </Typography>
                            </div>
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>{form.formState.errors['caregiver_id']?.message}</FormHelperText>
            </FormControl>
            <br />
            <br />
            <div className="location-container">
                <div style={{flex: 1}}>
                    <Typography variant="inputLabel" color="text.secondary">
                        Location
                    </Typography>
                    {loading || clientSkeleton ? (
                        <SkeletonContainer>
                            <TextSkeleton width={200} fontSize={16} />
                            <TextSkeleton width={140} fontSize={16} />
                        </SkeletonContainer>
                    ) : clientAddress ? (
                        <Typography variant="body1" color="text.primary" style={{maxWidth: 200}}>
                            {`${clientAddress?.street_line1}${clientAddress?.street_line2 ? ', ' + clientAddress?.street_line2 : ''}, ${
                                clientAddress?.city
                            }, ${clientAddress?.state}, ${clientAddress?.zip_code}`}
                        </Typography>
                    ) : (
                        <Typography variant="body1" color="text.primary" style={{fontStyle: 'italic'}}>
                            Select the client name
                        </Typography>
                    )}
                </div>
                {(loading || clientSkeleton) && <ButtonSkeleton width={105} />}
                {!loading && !clientSkeleton && clientAddress && (
                    <Button variant="outlined" startIcon={<Edit />} onClick={() => setOpen(true)}>
                        Update
                    </Button>
                )}
            </div>
            <AddressDetail open={open} setOpen={setOpen} clientAddress={clientAddress} setClientAddress={setClientAddress} />
        </>
    )
}
